import { useEffect, useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';

import { BaseButton, BaseDialog, type BaseAppearance } from '../base';

const oneTimeValueAppearance: BaseAppearance = {
  preset: 'soft',
  density: 'default',
  radius: 'lg',
  border: 'subtle',
  background: 'soft',
};

export interface OneTimeValueDialogLabels {
  show: string;
  hide: string;
  copy: string;
  close: string;
}

export interface OneTimeValueDialogProps {
  open: boolean;
  title: string;
  description: string;
  valueLabel: string;
  readValue(): string;
  clearValue(): void;
  labels: OneTimeValueDialogLabels;
  onCopy(value: string): void | Promise<void>;
  onClose(): void;
}

export const maskOneTimeValue = (value: string) => `${value.slice(0, 3)}${'*'.repeat(32)}${value.slice(-4)}`;

export function OneTimeValueDialog({
  open,
  title,
  description,
  valueLabel,
  readValue,
  clearValue,
  labels,
  onCopy,
  onClose,
}: OneTimeValueDialogProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    return clearValue;
  }, [clearValue]);

  const close = () => {
    clearValue();
    setVisible(false);
    onClose();
  };

  const copy = () => {
    const current = readValue();
    if (current) void onCopy(current);
  };

  return (
    <BaseDialog
      appearance={oneTimeValueAppearance}
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={title}
      description={description}
      closeLabel={labels.close}
      classNames={{ panel: 'sm:max-w-xl' }}
      footer={
        <BaseButton appearance={oneTimeValueAppearance} tone="primary" label={labels.close} onClick={close} />
      }
    >
      <div className="grid gap-4 py-2">
        <div className="rounded-md border border-border/70 bg-muted/40 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{valueLabel}</span>
            <div className="flex gap-2">
              <BaseButton
                appearance={oneTimeValueAppearance}
                tone="muted"
                iconOnly
                icon={visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                ariaLabel={visible ? labels.hide : labels.show}
                title={visible ? labels.hide : labels.show}
                onClick={() => setVisible((current) => !current)}
              />
              <BaseButton
                appearance={oneTimeValueAppearance}
                tone="muted"
                iconOnly
                icon={<Copy className="h-4 w-4" />}
                ariaLabel={labels.copy}
                title={labels.copy}
                onClick={copy}
              />
            </div>
          </div>
          <code className="block break-all rounded bg-background p-2 font-mono text-sm">
            {visible ? readValue() : maskOneTimeValue(readValue())}
          </code>
        </div>
      </div>
    </BaseDialog>
  );
}
